
import { useEffect, useState } from 'react';
import { AnimatedLogo } from './AnimatedLogo';
import { cn } from '@/lib/utils';

interface LoadingProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'logo' | 'spinner';
  fullScreen?: boolean;
  isInitialLoad?: boolean; // Flag for initial app loading
}

export function Loading({ 
  className, 
  size = 'md', 
  variant = 'logo', 
  fullScreen = false,
  isInitialLoad = false
}: LoadingProps) {
  // For initial app loading, auto-switch to spinner to improve performance
  const [useSimpleLoader, setUseSimpleLoader] = useState(false);
  
  useEffect(() => {
    // Check if this is an initial page load
    const isFirstLoad = !document.body.classList.contains('app-loaded');
    
    // Only use simple loader for non-logo variants
    if ((isFirstLoad || isInitialLoad) && variant !== 'logo') {
      setUseSimpleLoader(true);
    }
    
    // Mark the app as loaded after a delay
    setTimeout(() => {
      document.body.classList.add('app-loaded');
    }, 500);
  }, [isInitialLoad, variant]);
  
  // Size mappings
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  const logoSize = {
    sm: 32,
    md: 64,
    lg: 96
  };

  // Use full-screen dark background during initial load
  if (isInitialLoad) {
    // For logo variant with initial load
    if (variant === 'logo' && !useSimpleLoader) {
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-[#1A181B]">
          <AnimatedLogo
            size={logoSize[size]}
            className={className}
          />
        </div>
      );
    }

    // For spinner variant with initial load
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#1A181B]">
        <div className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-white border-t-transparent`} />
      </div>
    );
  }

  // For non-initial loads - use the regular approach
  const containerClasses = cn(
    'flex items-center justify-center',
    fullScreen ? 'min-h-[70vh]' : 'h-full'
  );

  // Use simple spinner for spinner variant
  if (variant === 'spinner' || useSimpleLoader) {
    return (
      <div className={containerClasses}>
        <div className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-primary border-t-transparent`} />
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <AnimatedLogo 
        size={logoSize[size]} 
        className={className}
      />
    </div>
  );
} 